import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { DoctorWorkingHour } from '@/types/settings-api';
import { doctorWorkingHoursListQueryKey } from './use-doctor-working-hours-list';

/**
 * Create a new working-hours profile for a practitioner (PROD-762 PR-D).
 *   POST /api/Doctor/AddWorkingHours
 *
 * Flutter parity: `addDoctorWorkingHours` in
 * `settings_data_source_impl.dart:1014`. Body shape verified against
 * working dev cURL 2026-05-19.
 *
 * **Asymmetry vs `useAddWorkingHours` (Practice path)**: Flutter
 * `addDoctorWorkingHours` does NOT strip any fields — it sends
 * `doctorWorkingHour.toJson()` whole. Compare with
 * `addPracticeWorkingHours` (line 1028) which removes `workingHourId`
 * + `practiceName`. Don't apply the practice-side stripping logic
 * here; the wire shape for Doctor expects the empty-string sentinels.
 *
 * Wire-shape notes (verified against cURL — do NOT regress):
 *   - `workingHourId` ships as `""` (empty string) on create, NOT
 *     omitted. Flutter `DoctorWorkingHour.toJson()` always emits it.
 *   - `practiceName` ships as `""` on create — backend derives from
 *     `practiceId`.
 *   - `organizationId` / `organizationTimezone` ship as `null` (NOT
 *     empty string). Backend fills from session.
 *   - Per-slot `id` ships as `""` (empty string) for new slots.
 *   - Per-slot `startHour` / `endHour` are HUMAN-FORMAT strings like
 *     `"12 AM"`, `"11 PM"` (NOT 24h).
 *   - Per-slot `startMinute` / `endMinute` carry the `" min"` suffix
 *     like `"00 min"`, `"55 min"` (NOT bare digits).
 *   - `startDate` / `endDate` are no-Z ISO datetimes with `.000`
 *     suffix (`"2026-05-19T00:00:00.000"`).
 *   - `locationIds: null` even when `locations[]` is populated.
 *   - `followupTimeSlot` / `timeSlot` are `""` when unset (NOT null).
 *
 * Caller is responsible for shaping the body — the
 * `WorkingHoursEditDialog` owns the draft and passes the canonical
 * `DoctorWorkingHour` shape directly.
 */
export function useAddDoctorWorkingHours(doctorSetupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DoctorWorkingHour) => {
      return post<unknown>('/api/Doctor/AddWorkingHours', body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: doctorWorkingHoursListQueryKey(doctorSetupId),
      });
    },
  });
}
